import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LayoutDashboard, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/data";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/directory", label: "Directory", testid: "nav-directory-link" },
  { to: "/leadership", label: "Leadership", testid: "nav-exco-link" },
  { to: "/events", label: "Events", testid: "nav-events-link" },
  { to: "/cultural-runway", label: "Nasarawa @30", testid: "nav-cultural-link" },
  { to: "/news", label: "News", testid: "nav-news-link" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass border-b border-white/10">
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <img src="/nasarawa-mark.png" alt="Nasarawa Expo" className="w-10 h-10 object-contain group-hover:rotate-6 transition-transform" />
          <div className="leading-none">
            <div className="font-heading font-extrabold text-[15px] tracking-tight">NASARAWA EXPO</div>
            <div className="text-[9px] uppercase tracking-[0.3em] brand-text-gradient font-bold mt-0.5">Arts · Music · Fashion</div>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${pathname === l.to ? "text-white bg-white/5" : "text-zinc-400 hover:text-white"}`}>
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard-link">
                <Button variant="ghost" size="sm" className="text-zinc-300 hover:text-white">
                  <LayoutDashboard className="w-4 h-4 mr-1.5" /> {ROLE_LABELS[user.role]}
                </Button>
              </Link>
              <Button onClick={doLogout} size="sm" variant="outline" className="border-white/15 bg-transparent" data-testid="nav-logout-button">
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login-button">
              <Button variant="ghost" size="sm" className="text-zinc-300 hover:text-white">Staff Login</Button>
            </Link>
          )}
          <Link to="/register" data-testid="nav-register-button">
            <Button size="sm" className="bg-[#E6197E] hover:bg-[#C4136B] text-white rounded-full px-5 font-semibold">Become a Member</Button>
          </Link>
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden glass border-t border-white/10 px-5 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="px-3 py-2.5 text-sm text-zinc-300 hover:text-white rounded-md">{l.label}</Link>
          ))}
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="px-3 py-2.5 text-sm text-zinc-300 hover:text-white">Dashboard</Link>
              <button onClick={() => { setOpen(false); doLogout(); }} className="px-3 py-2.5 text-sm text-left text-zinc-300">Logout</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2.5 text-sm text-zinc-300">Staff Login</Link>
          )}
          <Link to="/register" onClick={() => setOpen(false)} className="mt-2">
            <Button className="w-full bg-[#E6197E] hover:bg-[#C4136B] text-white rounded-full">Become a Member</Button>
          </Link>
        </div>
      )}
    </header>
  );
}
